import {LitElement, html, TemplateResult} from 'lit';
import {customElement, property} from 'lit/decorators.js';
import {fromAttribute} from './converter';
import {computeLocale} from '../../utils/compute-locale';

export type DnbDateTypes = 'date' |
  'time' |
  'datetime' |
  'yearmonth' |
  'year';

interface FormatOptions {
  locale?: string;
  options?: DateTimeFormatOptions;
}

/**
 * Date formatting element using configured locales
 *
 * @slot - Used to provide custom text to display.
 *
 * @attr {string} date The date string to represent.
 * @attr {string} locale What to display the instance in.
 */
@customElement('dnb-date')
export class DnbDateElement extends LitElement {
  /**
   * What to display the instance in.
   * If not defined, looks in localstorage for a prefernece.
   * If that is not defined, uses the browser's preference.
   *
   * @attr
   */
  @property()
  locale?: string;

  /**
   * The date the instance should display.
   *
   * @attr {string} ISO 8601 string in UTC. Not recommended.
   */
  @property({
    reflect: false,
    converter: {
      fromAttribute,
    },
  })
  date?: Date;

  /**
   * The configuration to use for displaying the data.
   */
  @property({
    reflect: false,
  })
  options: DateTimeFormatOptions;

  /**
   * Define the type of display to use.
   * Used primarily for inferred configuration scenarios.
   * Possible values are:
   * * 'date'
   * * 'time'
   * * 'yearmonth'
   * * 'year'
   * * 'datetime' - Default to show the most data.
   *
   * @attr
   */
  @property({
    attribute: true,
  })
  type: DnbDateTypes = 'datetime';

  /**
   * What string to show if the date does not exist
   * or is invalid. The default text to show is
   * "Invalid Date" from the browser.
   */
  @property({
    attribute: 'invalid-value',
  })
  invalidValue = 'Invalid Date';

  /**
   * Do not use a Shadow DOM.
   * This element is purely text manipulation.
   *
   * @return {this} Same object without a shadow root.
   */
  
  protected createRenderRoot(): Element {
    return this;
  }

  /**
   * Get the text value to display to a reader.
   *
   * @return {string} Local date string or 'Invalid Date'
   */
  get value(): string {
    return DnbDateElement.format(
        this.date,
        {
          locale: this.locale,
          options: this.computedOptions,
        },
    );
  }

  /**
   * Generate a computer-readable string based on the type
   * of value being displayed.
   *
   * @return {string} A machine-understandable temporal string.
   */
  get machineDateValue(): string {
    const isoString = this.date.toISOString();
    switch (this.type) {
      case 'date':
        return isoString.split('T')[0];
      case 'time':
        return isoString.split('T')[1];
      case 'yearmonth':
        return isoString.substring(0, 7);
      case 'year':
        return isoString.substring(0, 3);
      case 'datetime':
      default:
        return isoString;
    }
  }

  /**
   * Compile options based on attribute/property
   * configuration.
   */
  get computedOptions(): DateTimeFormatOptions {
    if (this.options !== undefined) {
      return this.options;
    }

    const {type} = this;

    const preferredOptions = localStorage.getItem(
        `dnb.preferences.format.${type}`,
    );

    if (preferredOptions !== null) {
      try {
        return JSON.parse(preferredOptions);
      } catch (e) {
        console.warn(
            'Failed to parse preferred options, resuming with default',
        );
      }
    }

    switch (type) {
      case 'date':
        return {
          dateStyle: 'medium',
          timeZone: 'Etc/UTC',
        };
      case 'time':
        return {
          timeStyle: 'short',
          timeZone: 'Etc/UTC',
        };
      case 'yearmonth':
        return {
          month: '2-digit',
          year: 'numeric',
          timeZone: 'Etc/UTC',
        };
      case 'year':
        return {
          year: 'numeric',
          timeZone: 'Etc/UTC',
        };
      case 'datetime':
      default:
        return {
          dateStyle: 'medium',
          timeStyle: 'short',
          timeZone: 'Etc/UTC',
        };
    }
  }

  /**
   * Format a date value into a localized string.
   *
   * @param value The value to be localized.
   * @param formatOptions The locale and options to localize
   * with.
   *
   * @return The provided value as a local formatted string.
   */
  static format(
      value: Date,
      formatOptions: FormatOptions = {
        locale: undefined,
        options: {},
      },
  ): string {
    const {locale, options} = formatOptions;

    return new Intl.DateTimeFormat(
        computeLocale(locale),
        options as Intl.DateTimeFormatOptions,
    ).format(value);
  }

  /**
   * Create the markup template to render in the DOM.
   *
   * @return {TemplateResult} Template content
   */
  render(): TemplateResult {
    if (
      this.date === null ||
      this.date === undefined ||
      this.date?.toString() === 'Invalid Date'
    ) {
      return html`<span>${this.invalidValue}</span>`;
    }

    return html`
      <time datetime="${this.machineDateValue}">
        ${this.value}
      </time>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'dnb-date': DnbDateElement,
  }
}
